import { useContext, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { ProductContext } from "../../../context/ProductContext";
import { useNavigation } from "../../../utils/navigation";
import { AuthUserContext } from "../../../context/AuthUserContext";
import { useTranslation } from "react-i18next";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Container = styled.div`
  width: 100%;
  background-color: var(--bg-200);
  overflow: hidden;
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;
const GridContainer = styled.div`
  min-width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  padding: var(--spacing-md) 0;
`;

/* --- Category quick access: outlined pill chips, 3 per row --- */
const CategoryGrid = styled.div`
  align-self: center;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 94%;
  margin: var(--spacing-sm) auto var(--spacing-md);
`;
const CategoryGridItem = styled.div`
  border-radius: 20px;
  border: 1.5px solid var(--accent-200);
  background-color: transparent;
  color: var(--text-200);
  padding: 10px 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-transform: uppercase;
  font-size: var(--font-size-small);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background-color: var(--accent-200);
    color: var(--bg-100);
  }
`;

/* --- Main nav: spaced-out rounded cards instead of flush full-width rows --- */
const NavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 94%;
  margin: 0 auto;
`;
const NavCard = styled.button`
  width: 100%;
  padding: 14px 16px;
  border-radius: 10px;
  background-color: var(--bg-100);
  border: none;
  color: var(--text-200);
  font-size: var(--font-size-base);
  font-weight: 500;
  text-align: left;
  text-transform: uppercase;
  display: flex;
  justify-content: space-between;
  align-items: center;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: var(--bg-100);
  }
  &:active {
    background-color: var(--bg-300);
  }
`;
const Chevron = styled.svg`
  transition: transform 0.25s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(90deg)" : "rotate(0)")};
`;

/* --- Brands accordion: slide-down max-height, not clip-path --- */
const AccordionPanel = styled.div`
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? "2000px" : "0px")};
  transition: max-height 0.35s ease;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2px;
  padding-top: ${({ $isOpen }) => ($isOpen ? "2px" : "0")};
`;
const BrandRow = styled(Link)`
  display: block;
  padding: 12px;
  text-decoration: none;
  color: var(--text-200);
  background-color: var(--bg-100);
  border-left: 3px solid var(--accent-200);
  animation: ${fadeIn} 0.2s ease both;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background-color: var(--bg-300);
  }
`;

/* --- Guest CTA / single account entry point --- */
const ButtonShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex-shrink: 0;
  width: 94%;
  margin: 0 auto;
`;
const TButton = styled.button`
  margin-top: 14px;
  width: 100%;
`;
const LButton = styled(TButton)`
  background-color: var(--bg-200);
  border: none;
  color: var(--background-color);
`;

const Logo = styled.div`
  color: var(--text-200);
  font-size: var(--font-size-xlarge);
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.438px;
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
  cursor: pointer;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 14px;
  margin-bottom: var(--spacing-sm);
`;

const HamburgerMenuData = ({ setIsOpen }) => {
  const { t } = useTranslation();
  const { categories } = useContext(ProductContext);
  const { authTokens } = useContext(AuthUserContext);
  const navigate = useNavigate();
  const {
    goToCategory,
    goToHome,
    goToShop,
    goToBestSeller,
    goToNewArrivals,
    goToLogin,
    goToRegister,
    goToFeatured,
    goToAccount,
  } = useNavigation();

  const [isBrandsOpen, setIsBrandsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(false);
  };

  const toggleCategory = (event, category) => {
    event.preventDefault();
    navigate(goToCategory(category.name));
    toggleMenu();
  };

  return (
    <Container>
      <Wrapper>
        <GridContainer>
          <CategoryGrid>
            {categories.slice(0, 6).map((category, index) => (
              <CategoryGridItem
                key={index}
                onClick={(event) => toggleCategory(event, category)}
              >
                {category.name}
              </CategoryGridItem>
            ))}
          </CategoryGrid>

          <NavList>
            <NavCard
              onClick={() => {
                navigate(goToFeatured());
              }}
            >
              {t("HEADER.LINKS.NEW_YEAR_NEW_ME")}
            </NavCard>

            <NavCard
              onClick={() => {
                navigate(goToShop());
              }}
            >
              {t("MENU.NICOTINE_POUCHES")}
            </NavCard>

            <NavCard onClick={() => setIsBrandsOpen((prev) => !prev)}>
              {t("MENU.BRANDS")}
              <Chevron
                $isOpen={isBrandsOpen}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 6l6 6-6 6"
                  stroke="var(--text-200)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Chevron>
            </NavCard>
            <AccordionPanel $isOpen={isBrandsOpen}>
              {categories &&
                categories.length > 0 &&
                categories.slice(0, 100).map((category, index) => (
                  <BrandRow
                    key={index}
                    to="#"
                    onClick={(event) => toggleCategory(event, category)}
                  >
                    {category.name}
                  </BrandRow>
                ))}
            </AccordionPanel>

            <NavCard onClick={() => navigate(goToBestSeller())}>
              {t("MENU.BEST_SELLERS")}
            </NavCard>

            <NavCard onClick={() => navigate(goToNewArrivals())}>
              {t("MENU.NEW_ARRIVALS")}
            </NavCard>
          </NavList>
        </GridContainer>

        {authTokens ? (
          <ButtonShell>
            <TButton onClick={() => navigate(goToAccount())}>
              {t("PROFILE.ACCOUNT_OVERVIEW")}
            </TButton>
          </ButtonShell>
        ) : (
          <ButtonShell>
            <LButton
              onClick={() => {
                navigate(goToRegister());
              }}
            >
              {t("MENU.SIGN_UP")}
            </LButton>
            <TButton onClick={() => navigate(goToLogin())}>
              {t("MENU.LOGIN")}
            </TButton>
          </ButtonShell>
        )}

        <Logo onClick={() => navigate(goToHome())}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 298 298"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M148.707 0C66.7245 0 0 66.7245 0 148.707C0 230.689 66.7245 297.414 148.707 297.414C230.689 297.414 297.414 230.689 297.414 148.707C297.414 66.7245 230.689 0 148.707 0ZM294.707 148.707C294.707 188.275 278.843 224.204 253.18 250.567L244.688 242.074C268.158 217.904 282.669 184.962 282.669 148.707C282.669 112.452 267.878 78.8562 243.988 54.6394L252.48 46.1472C278.563 72.557 294.661 108.766 294.661 148.707H294.707ZM17.3576 148.707C17.3576 76.2899 76.2432 17.4043 148.66 17.4043C221.077 17.4043 279.963 76.2899 279.963 148.707C279.963 221.124 221.077 280.009 148.66 280.009C76.2432 280.009 17.3576 221.124 17.3576 148.707ZM250.52 44.2808L242.028 52.7731C217.858 29.2562 184.915 14.7447 148.614 14.7447C112.312 14.7447 79.416 29.2562 55.2458 52.7731L46.7536 44.2808C73.0701 18.6176 108.999 2.75295 148.567 2.75295C188.135 2.75295 224.11 18.6175 250.427 44.3275L250.52 44.2808ZM44.7939 46.1939L53.2862 54.6862C29.3494 78.9029 14.558 112.172 14.558 148.8C14.558 185.429 29.0693 218.044 52.5862 242.214L44.1406 250.707C18.4307 224.39 2.56627 188.415 2.56627 148.847C2.56627 109.279 18.7107 72.6504 44.7939 46.2872V46.1939ZM46.0539 252.667L54.5461 244.174C78.7629 268.064 111.985 282.856 148.614 282.856C185.242 282.856 218.511 268.064 242.728 244.128L251.22 252.62C224.857 278.703 188.602 294.848 148.66 294.848C108.719 294.848 72.5102 278.703 46.147 252.667"
              fill="var(--text-200)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M207.722 57.7351C202.622 66.9627 200.922 78.8759 205.416 87.3302C214.54 104.491 230.158 114.314 239.894 131.851C273.15 191.781 209.011 201.72 174.784 209.915C155.428 214.537 143.812 230.506 158.492 258.688C214.436 253.98 258.39 207.07 258.39 149.903C258.39 111.144 238.177 77.1018 207.722 57.7266"
              fill={"var(--text-200)"}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M191.927 105.506C177.373 95.2564 162.525 82.7355 160.972 63.8935C160.307 55.8558 164.875 48.4915 171.632 43.0459C157.459 40.0907 142.84 39.9776 128.623 42.713C114.406 45.4483 100.873 50.9779 88.8086 58.9812C96.9207 91.2874 127.561 94.6763 155.629 103.143C195.722 115.235 214.322 153.656 158.871 159.818C81.4401 168.423 72.2954 187.545 127.861 256.987C133.447 258.095 139.112 258.763 144.803 258.984C114.222 212.039 131.18 198.9 188.95 180.965C245.166 163.485 224.646 128.565 191.925 105.51"
              fill="var(--text-100)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M123.936 118.509C100.162 114.213 67.312 112.348 58.2119 97.5595C57.3549 96.166 56.5939 94.7157 55.9342 93.2188C45.5264 110.296 40.0342 129.915 40.0626 149.915C40.0626 197.952 71.1131 238.761 114.236 253.341C95.0693 233.567 63.4812 210.21 71.1047 172.043C76.204 146.547 105.038 150.269 127.675 148.404C160.298 145.722 156.82 124.463 123.927 118.509"
              fill={"var(--text-200)"}
            />
          </svg>
          SnusCo
        </Logo>
      </Wrapper>
    </Container>
  );
};

export default HamburgerMenuData;
